import { Box, Checkbox, styled } from '@mui/material'
import { useTheme } from '@mui/material'
import React from 'react'
import { GridColDef } from '@mui/x-data-grid-pro'
import GenericTable from 'src/components/table/GenericTable'
import { GetRGBA, getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'
import { useTranslation } from 'react-i18next'
import { IMainComponentTypeFormSchema } from 'src/generated-types/main-component-type'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { DeleteOutlineOutlined } from '@mui/icons-material'
import { FormWatch } from './ChecklistAssignmentSection'

interface ChecklistAssignmentsTableProps {
  selectedChecklists: any[]
  onToggleMovement: (formSchemaId: number, movementType: number, checked: boolean) => void
  onRemoveChecklist: (checklistId: number) => void
  formWatch: FormWatch
}

export const ChecklistAssignmentsTable: React.FC<ChecklistAssignmentsTableProps> = ({
  selectedChecklists,
  onToggleMovement,
  onRemoveChecklist,
  formWatch,
}) => {
  const theme = useTheme()
  const { t: translate } = useTranslation(['main-component-type', 'common'])

  const checklistColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: translate('checklistTemplate', { ns: 'main-component-type' }),
      flex: 2,
    },
    {
      field: 'SendToLocality',
      headerName: translate('SendToLocality', { ns: 'activity-log-type' }),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => {
        const schemaId = params.row.id as number
        const selectedMovements =
          ((formWatch('mainComponentTypeFormSchemas') as IMainComponentTypeFormSchema[] | undefined) ?? []).find(
            mapping => mapping.formSchemaId === schemaId,
          )?.movementTypes ?? []

        return (
          <CenteredCell>
            <Checkbox
              checked={selectedMovements.includes(0)}
              onChange={(_, checked) => onToggleMovement(schemaId, 0, checked)}
            />
          </CenteredCell>
        )
      },
    },
    {
      field: 'SendToServiceStation',
      headerName: translate('SendToServiceStation', { ns: 'activity-log-type' }),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => {
        const schemaId = params.row.id as number
        const selectedMovements =
          ((formWatch('mainComponentTypeFormSchemas') as IMainComponentTypeFormSchema[] | undefined) ?? []).find(
            mapping => mapping.formSchemaId === schemaId,
          )?.movementTypes ?? []

        return (
          <CenteredCell>
            <Checkbox
              checked={selectedMovements.includes(1)}
              onChange={(_, checked) => onToggleMovement(schemaId, 1, checked)}
            />
          </CenteredCell>
        )
      },
    },
    {
      field: 'SendToStorage',
      headerName: translate('SendToStorage', { ns: 'activity-log-type' }),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => {
        const schemaId = params.row.id as number
        const selectedMovements =
          ((formWatch('mainComponentTypeFormSchemas') as IMainComponentTypeFormSchema[] | undefined) ?? []).find(
            mapping => mapping.formSchemaId === schemaId,
          )?.movementTypes ?? []

        return (
          <CenteredCell>
            <Checkbox
              checked={selectedMovements.includes(2)}
              onChange={(_, checked) => onToggleMovement(schemaId, 2, checked)}
            />
          </CenteredCell>
        )
      },
    },
    {
      field: 'moveToPosition',
      headerName: translate('move-to-position', { ns: 'main-component' }),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => {
        const schemaId = params.row.id as number
        const selectedMovements =
          ((formWatch('mainComponentTypeFormSchemas') as IMainComponentTypeFormSchema[] | undefined) ?? []).find(
            mapping => mapping.formSchemaId === schemaId,
          )?.movementTypes ?? []

        return (
          <CenteredCell>
            <Checkbox
              checked={selectedMovements.includes(3)}
              onChange={(_, checked) => onToggleMovement(schemaId, 3, checked)}
            />
          </CenteredCell>
        )
      },
    },
    {
      field: 'release',
      headerName: translate('release', { ns: 'main-component' }),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => {
        const schemaId = params.row.id as number
        const selectedMovements =
          ((formWatch('mainComponentTypeFormSchemas') as IMainComponentTypeFormSchema[] | undefined) ?? []).find(
            mapping => mapping.formSchemaId === schemaId,
          )?.movementTypes ?? []

        return (
          <CenteredCell>
            <Checkbox
              checked={selectedMovements.includes(4)}
              onChange={(_, checked) => onToggleMovement(schemaId, 4, checked)}
            />
          </CenteredCell>
        )
      },
    },
    {
      field: 'actions',
      headerName: translate('actions', { ns: 'common' }),
      flex: 0.5,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      renderCell: params => (
        <GridActionButton
          width={'24px'}
          height={'24px'}
          toolTipTitle={translate('delete', { ns: 'common' })}
          onClick={() => onRemoveChecklist(params.row.id as number)}
          buttonIcon={<DeleteOutlineOutlined fontSize="small" />}
        />
      ),
    },
  ]

  return (
    <ChecklistTableContainer>
      <GenericTable
        id="checklist-assignments"
        columns={checklistColumns}
        data={selectedChecklists}
        noRowsMessage={translate('noChecklistTemplates', { ns: 'main-component-type' })}
        getRowHeight={() => 44}
      />
    </ChecklistTableContainer>
  )
}

const ChecklistTableContainer = styled(Box)(({ theme }) => ({
  border: `1px solid ${GetRGBA(getThemeColor(theme, MUIThemeColors.secondaryMain), 0.4)}`,
  borderRadius: 4,
  width: '100%',
}))

const CenteredCell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
})
